import React, { useState, useMemo } from "react";
import CameraView from "../../ScanningApp/components/CameraView";
import { GetSystemSettingValue } from "../../../common/service/CommonService";
import ScanOverlay from "../../ScanningApp/components/ScanOverlay";
import resStyles from "../css/scanningSessionResponsive.module.scss";

interface MobileStep1ScannerProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  torchOn: boolean;
  toggleTorch: () => void;
  subjectBarcode: string;
  subjectStatus: "idle" | "matched" | "mismatch";
  vacutainerBarcode: string;
  vacutainerStatus: "idle" | "matched" | "mismatch";
  handleBarcodeSubmit: (barcode: string) => void;
  clearAndRestart: () => void;
  formMessage: { text: string; type: "success" | "error" | "" };
  completedSubs: number;
  totalSubs: number;
  skippedSubs?: number;
  onCollectClick?: () => void;
  isCollectScanning?: boolean;
  onSkipClick?: () => void;
  isSkipEnabled?: boolean;
  sessionData?: any;
  isProcessing?: boolean;
}

interface ScannerVideoBlockProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  torchOn: boolean;
  toggleTorch: () => void;
  formMessage: { text: string; type: "success" | "error" | "" };
}

const ScannerVideoBlock: React.FC<ScannerVideoBlockProps> = ({
  videoRef,
  torchOn,
  toggleTorch,
  formMessage,
}) => (
  <div className={resStyles.videoWrapper}>
    <CameraView videoRef={videoRef} />
    <ScanOverlay />
    {formMessage.text && (
      <div className={`${resStyles.scannerFeedbackOverlay} ${resStyles[formMessage.type]}`}>
        <i className={formMessage.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}></i>
        <span>{formMessage.text}</span>
      </div>
    )}
    <button
      type="button"
      className={`${resStyles.torchBtn} ${torchOn ? resStyles.torchOn : ""}`}
      onClick={toggleTorch}
      aria-label={torchOn ? "Turn off torch" : "Turn on torch"}
    >
      <i className={torchOn ? "ri-flashlight-fill" : "ri-flashlight-line"}></i>
    </button>
  </div>
);

const MobileScanningStep1Scanner: React.FC<MobileStep1ScannerProps> = ({
  videoRef,
  torchOn,
  toggleTorch,
  subjectBarcode,
  subjectStatus,
  vacutainerBarcode,
  vacutainerStatus,
  handleBarcodeSubmit,
  clearAndRestart,
  formMessage,
  completedSubs,
  totalSubs,
  skippedSubs = 0,
  onCollectClick,
  isCollectScanning = false,
  onSkipClick,
  isSkipEnabled = false,
  sessionData,
  isProcessing = false,
}) => {
  const [manualBarcode, setManualBarcode] = useState("");

  const showCollectBtn = useMemo(
    () => (GetSystemSettingValue(1) || GetSystemSettingValue("Actual Time Collection Method") || "Scanning App") === "Scanning App",
    []
  );

  const sampleCollectionMode = useMemo(
    () => GetSystemSettingValue("Sample Collection Mode") || GetSystemSettingValue("Collection Capture Mode") || GetSystemSettingValue("Collection Mode") || "Auto",
    []
  );

  const isAutoCollect = useMemo(
    () => sampleCollectionMode.toLowerCase() === "auto",
    [sampleCollectionMode]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualBarcode.trim() || isProcessing) return;
    handleBarcodeSubmit(manualBarcode.trim());
    setManualBarcode("");
  };

  const handleClear = () => {
    if (isProcessing) return;
    setManualBarcode("");
    clearAndRestart();
  };

  const totalSubjectsCount = totalSubs || sessionData?.total_subjects || sessionData?.totalsubjects || 0;
  const actualCompletedVacutainers = sessionData?.completed_vaccutainers ?? 0;

  const isStep1Completed =
    (totalSubjectsCount > 0 && actualCompletedVacutainers >= totalSubjectsCount && vacutainerStatus !== "matched" && !isCollectScanning) ||
    sessionData?.display_status === "COMPLETED" ||
    sessionData?.sessionstate === "completed";

  if (isStep1Completed) {
    return (
      <div className={`${resStyles.scanCard} ${resStyles.successCard}`} style={{ borderColor: "#16a34a", backgroundColor: "#f0fdf4", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 20px", textAlign: "center" }}>
        <div className={resStyles.checkmarkWrapper} style={{ backgroundColor: "#16a34a", color: "#ffffff", width: "56px", height: "56px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", marginBottom: "16px" }}>
          <i className="ri-checkbox-circle-fill"></i>
        </div>
        <h6 className="fw-bold text-success mb-2" style={{ fontSize: "18px" }}>Subject to Vacutainer Completed</h6>
        <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
          All {totalSubjectsCount} subjects and their vacutainers have been successfully matched and scanned for this session.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* ── SKIP ACTION ROW (Between Header & Subject Scan Window) ── */}
      {onSkipClick && (
        <div className="d-flex justify-content-end align-items-center w-100 mb-0 px-1">
          <button
            type="button"
            className="btn btn-outline-warning text-dark fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
            style={{
              borderRadius: "6px",
              fontSize: "12.5px",
              padding: "4px 14px",
              backgroundColor: isSkipEnabled && !isProcessing ? "#fff8e6" : "#f8fafc",
              borderColor: isSkipEnabled && !isProcessing ? "#f59e0b" : "#cbd5e1",
              color: isSkipEnabled && !isProcessing ? "#b45309" : "#94a3b8",
              cursor: isSkipEnabled && !isProcessing ? "pointer" : "not-allowed",
              opacity: isSkipEnabled && !isProcessing ? 1 : 0.65,
              transition: "all 0.2s ease-in-out",
            }}
            onClick={onSkipClick}
            disabled={!isSkipEnabled || isProcessing}
            title={isProcessing ? "Please wait..." : (isSkipEnabled ? "Skip current subject" : "Scan subject barcode first to enable Skip")}
          >
            <i className="ri-skip-forward-line fs-14 text-warning"></i>
            {isProcessing ? "Processing..." : "Skip Subject"}
          </button>
        </div>
      )}

      {/* ── STEP 1: CARD 1: SUBJECT ── */}
      {subjectStatus === "matched" ? (
        <div className={`${resStyles.scanCard} ${resStyles.successCard}`}>
          <div className={resStyles.checkmarkWrapper}>
            <i className="ri-checkbox-circle-fill"></i>
          </div>
          <div className={resStyles.successInfo}>
            <div className={resStyles.barcodeValue}>
              Barcode: {subjectBarcode}
            </div>
            <div className={resStyles.successLabel}>- VALID SUBJECT</div>
          </div>
        </div>
      ) : (
        <div className={`${resStyles.scanCard} ${resStyles.activeCard}`}>
          <div className={resStyles.cardHeader}>
            <div className={resStyles.cardHeaderIcon}>
              <i className="ri-qr-scan-2-line"></i>
            </div>
            <div className={resStyles.cardHeaderTitles}>
              <h6>SCAN SUBJECT</h6>
              <p>Position the subject barcode inside the camera view frame</p>
            </div>
          </div>
          <ScannerVideoBlock
            videoRef={videoRef}
            torchOn={torchOn}
            toggleTorch={toggleTorch}
            formMessage={formMessage}
          />

          <form className={resStyles.cardManualInputRow} onSubmit={handleSubmit}>
            <input
              type="text"
              aria-label="Subject barcode"
              className={resStyles.cardManualInput}
              placeholder="Enter Subject Barcode..."
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              disabled={isProcessing}
            />
            <div className={resStyles.cardButtonRow}>
              <button
                type="submit"
                className={resStyles.cardOkBtn}
                style={{ minWidth: "48px" }}
                disabled={!manualBarcode.trim() || isProcessing}
              >
                {isProcessing ? "..." : "OK"}
              </button>
              <button
                type="button"
                className={resStyles.cardClearBtn}
                onClick={handleClear}
                disabled={isProcessing}
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 1: CARD 2: VACUTAINER / SAMPLE COLLECTION SCAN ── */}
      {subjectStatus !== "matched" ? (
        <div className={`${resStyles.scanCard} ${resStyles.lockedCard}`}>
          <div className={resStyles.lockIconWrapper}>
            <i className="ri-lock-fill"></i>
          </div>
          <div className={resStyles.lockedLabel}>SCAN VACUTAINER BARCODE</div>
          <div className={resStyles.lockedSublabel}>(WAITING)</div>
        </div>
      ) : isCollectScanning ? (
        /* AUTO MODE SECOND SCAN: RE-SCAN VACUTAINER TO COLLECT SAMPLE */
        <div className={`${resStyles.scanCard} ${resStyles.activeCard} ${resStyles.vacutainerActive}`}>
          <div className={resStyles.cardHeader}>
            <div className={`${resStyles.cardHeaderIcon} ${resStyles.vacutainerIcon}`} style={{ backgroundColor: "#0d9488", color: "#ffffff" }}>
              <i className="ri-drop-line"></i>
            </div>
            <div className={resStyles.cardHeaderTitles}>
              <h6 style={{ color: "#0d9488", fontWeight: 700 }}>SCAN VACUTAINER TO COLLECT SAMPLE</h6>
              <p>Re-scan vacutainer barcode to record sample collection time</p>
            </div>
          </div>
          <ScannerVideoBlock
            videoRef={videoRef}
            torchOn={torchOn}
            toggleTorch={toggleTorch}
            formMessage={formMessage}
          />

          <form className={resStyles.cardManualInputRow} onSubmit={handleSubmit}>
            <input
              type="text"
              aria-label="Vacutainer barcode"
              className={resStyles.cardManualInput}
              placeholder="Scan / Enter Vacutainer Barcode to Collect..."
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              disabled={isProcessing}
            />
            <div className={resStyles.cardButtonRow}>
              <button
                type="submit"
                className={`${resStyles.cardOkBtn} ${resStyles.vacutainerBtn}`}
                style={{ backgroundColor: "#0d9488", borderColor: "#0d9488", minWidth: "75px" }}
                disabled={!manualBarcode.trim() || isProcessing}
              >
                {isProcessing ? "..." : "COLLECT"}
              </button>
              <button
                type="button"
                className={resStyles.cardClearBtn}
                onClick={handleClear}
                disabled={isProcessing}
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div
          className={`${resStyles.scanCard} ${resStyles.activeCard} ${resStyles.vacutainerActive}`}
        >
          <div className={resStyles.cardHeader}>
            <div
              className={`${resStyles.cardHeaderIcon} ${resStyles.vacutainerIcon}`}
            >
              <i className="ri-flask-line"></i>
            </div>
            <div className={resStyles.cardHeaderTitles}>
              <h6>SCAN VACUTAINER</h6>
              <p>Position the vacutainer barcode inside the camera view frame</p>
            </div>
          </div>

          {vacutainerStatus !== "matched" && (
            <ScannerVideoBlock
              videoRef={videoRef}
              torchOn={torchOn}
              toggleTorch={toggleTorch}
              formMessage={formMessage}
            />
          )}

          {vacutainerStatus !== "matched" ? (
            <form className={resStyles.cardManualInputRow} onSubmit={handleSubmit}>
              <input
                type="text"
                aria-label="Vacutainer barcode"
                className={resStyles.cardManualInput}
                placeholder="Enter Vacutainer Barcode..."
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e.target.value)}
                disabled={isProcessing}
              />
              <div className={resStyles.cardButtonRow}>
                <button
                  type="submit"
                  className={`${resStyles.cardOkBtn} ${resStyles.vacutainerBtn}`}
                  style={{ minWidth: "48px" }}
                  disabled={!manualBarcode.trim() || isProcessing}
                >
                  {isProcessing ? "..." : "OK"}
                </button>
                <button
                  type="button"
                  className={resStyles.cardClearBtn}
                  onClick={handleClear}
                  disabled={isProcessing}
                >
                  Clear
                </button>
              </div>
            </form>
          ) : (
            onCollectClick && showCollectBtn && (
              <div className="w-100 px-3 pb-3" style={{ marginTop: "10px" }}>
                <div className="alert alert-success d-flex align-items-center gap-2 py-2 px-3 mb-2" style={{ borderRadius: "6px", fontSize: "13px" }}>
                  <i className="ri-checkbox-circle-fill fs-16 text-success"></i>
                  <span>Subject & Vacutainer Match Verified!</span>
                </div>
                <button
                  type="button"
                  className="btn btn-teal w-100 d-inline-flex align-items-center justify-content-center gap-2 text-white"
                  style={{
                    backgroundColor: "#0d9488",
                    borderColor: "#0d9488",
                    borderRadius: "6px",
                    fontWeight: "600",
                    fontSize: "14px",
                    boxShadow: "0 2px 4px rgba(13, 148, 136, 0.15)",
                    padding: "10px 16px",
                    opacity: isProcessing ? 0.7 : 1,
                    cursor: isProcessing ? "not-allowed" : "pointer",
                  }}
                  onClick={onCollectClick}
                  disabled={isProcessing}
                >
                  <i className="ri-drop-line fs-18"></i>
                  {isProcessing ? "Processing..." : "Collect Sample"}
                </button>
              </div>
            )
          )}
        </div>
      )}
    </>
  );
};

export default MobileScanningStep1Scanner;
